/**
 * CareQueueTab — Post-settlement Phase 2.
 *
 * Shows upcoming + overdue care tasks (welcome call, 6-week check-in,
 * quarterly review, annual review) assigned to the current user.
 *
 * Each row supports two actions:
 *   ✓ Complete — marks the task done, logs an activity on the loan,
 *                and bumps last_contact_at when it's a contact channel.
 *   💤 Snooze  — pushes scheduled_at by 7 / 14 / 30 days.
 *
 * Clicking the client name opens the LoanDrawer.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Phone, Mail, MessageSquare, Calendar, Check, Clock, FileText, Copy, ClipboardCheck } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const TYPE_ICONS = { call: Phone, sms: MessageSquare, email: Mail, meeting: Calendar, review: FileText };

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

const daysUntil = (iso) => {
  if (!iso) return null;
  const target = new Date(iso);
  const now = new Date();
  const diff = Math.round((target - now) / (1000 * 60 * 60 * 24));
  return diff;
};

const CareQueueTab = ({ onOpenLoan }) => {
  const { token } = useAuth();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [scope, setScope] = useState('mine'); // mine | team

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days_ahead: '120', include_overdue: 'true', mine_only: scope === 'mine' ? 'true' : 'false' });
      const r = await fetch(`${backendUrl}/api/settled-loans/care/queue?${params}`, { headers: authHeaders });
      if (r.ok) setTasks(await r.json());
    } finally { setLoading(false); }
  }, [backendUrl, authHeaders, scope]);

  useEffect(() => { load(); }, [load]);

  const completeTask = async (id) => {
    setBusyId(id);
    try {
      const r = await fetch(`${backendUrl}/api/settled-loans/care-tasks/${id}/complete`, { method: 'POST', headers: authHeaders });
      if (!r.ok) throw new Error('Complete failed');
      setTasks((prev) => prev.filter((t) => t.id !== id));
      toast.success('Marked done — activity logged');
    } catch (e) { toast.error(e.message); } finally { setBusyId(null); }
  };

  const snoozeTask = async (id, days) => {
    setBusyId(id);
    try {
      const r = await fetch(`${backendUrl}/api/settled-loans/care-tasks/${id}/snooze?days=${days}`, { method: 'POST', headers: authHeaders });
      if (!r.ok) throw new Error('Snooze failed');
      setTasks((prev) => prev.filter((t) => t.id !== id));
      toast.success(`Snoozed ${days} day${days > 1 ? 's' : ''}`);
    } catch (e) { toast.error(e.message); } finally { setBusyId(null); }
  };

  const copyEmail = async (t) => {
    if (!t.template_code) {
      toast.error('No email template linked to this task — open the Templates tab to copy manually');
      return;
    }
    setBusyId(t.id);
    try {
      // Look up the active template id by code.
      const tplListRes = await fetch(`${backendUrl}/api/settled-loans/templates?active_only=true`, { headers: authHeaders });
      if (!tplListRes.ok) throw new Error('Could not load templates');
      const list = await tplListRes.json();
      const tpl = list.find((x) => x.code === t.template_code);
      if (!tpl) throw new Error(`Template ${t.template_code} not found`);
      // Render against this loan.
      const r = await fetch(
        `${backendUrl}/api/settled-loans/templates/${tpl.id}/render?loan_id=${encodeURIComponent(t.loan_id)}`,
        { method: 'POST', headers: authHeaders },
      );
      if (!r.ok) throw new Error('Render failed');
      const j = await r.json();
      const full = `Subject: ${j.subject}\n\n${j.body_with_footer}`;
      // Clipboard write.
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(full);
      } else {
        const ta = document.createElement('textarea');
        ta.value = full;
        ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
      }
      setCopiedId(t.id);
      setTimeout(() => setCopiedId(null), 2000);
      toast.success(`${t.template_code} copied — paste into your email`);
    } catch (e) { toast.error(e.message); } finally { setBusyId(null); }
  };

  return (
    <div className="space-y-3" data-testid="care-queue-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1">
          {[{ k: 'mine', label: 'Mine' }, { k: 'team', label: 'Team' }].map(({ k, label }) => (
            <button
              key={k}
              type="button"
              onClick={() => setScope(k)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                scope === k ? 'bg-blue-600 text-white border-blue-600' : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:border-blue-300'
              }`}
              data-testid={`care-scope-${k}`}
            >
              {label}
            </button>
          ))}
        </div>
        <span className="text-xs text-slate-500">{tasks.length} task{tasks.length === 1 ? '' : 's'} pending</span>
      </div>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-blue-600 animate-spin" /></div>
        ) : tasks.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-500">
            <Check className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
            <p className="font-semibold text-slate-700 dark:text-slate-200 mb-1">All caught up</p>
            <p className="text-xs">No care tasks due in the next 120 days{scope === 'mine' ? ' for you' : ''}.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {tasks.map((t) => {
              const Icon = TYPE_ICONS[t.type] || FileText;
              const dleft = daysUntil(t.scheduled_at);
              const overdue = t.overdue;
              return (
                <li key={t.id} className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors" data-testid="care-queue-row">
                  <div className={`h-9 w-9 rounded-xl grid place-items-center shrink-0 ${overdue ? 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300' : 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300'}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => onOpenLoan?.(t.loan_id)}
                        className="font-semibold text-slate-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-300 text-sm truncate"
                      >
                        {t.client_name}
                      </button>
                      <span className="font-mono text-[10px] text-slate-400">{t.loan_number}</span>
                      {overdue && <span className="px-1.5 py-0.5 rounded-full text-[9px] uppercase font-bold tracking-wide bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300">Overdue</span>}
                    </div>
                    <div className="text-xs text-slate-600 dark:text-slate-300 mt-0.5 flex items-center gap-1.5 flex-wrap">
                      <span>{t.title}</span>
                      {t.template_code && (
                        <span className="px-1.5 py-0.5 rounded-full text-[9px] uppercase font-mono font-bold bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300">
                          {t.template_code}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {fmtDate(t.scheduled_at)} · {overdue ? `${Math.abs(dleft)}d overdue` : dleft === 0 ? 'today' : `in ${dleft}d`}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {t.template_code && (
                      <button
                        type="button"
                        disabled={busyId === t.id}
                        onClick={() => copyEmail(t)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold disabled:opacity-50"
                        data-testid="care-copy-email-btn"
                        title={`Copy ${t.template_code} email to clipboard`}
                      >
                        {busyId === t.id ? <Loader2 className="w-3 h-3 animate-spin" /> : copiedId === t.id ? <ClipboardCheck className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                        {copiedId === t.id ? 'Copied' : 'Copy email'}
                      </button>
                    )}
                    <SnoozeMenu disabled={busyId === t.id} onPick={(d) => snoozeTask(t.id, d)} />
                    <button
                      type="button"
                      disabled={busyId === t.id}
                      onClick={() => completeTask(t.id)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold disabled:opacity-50"
                      data-testid="care-complete-btn"
                    >
                      {busyId === t.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Done
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

const SnoozeMenu = ({ onPick, disabled }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((s) => !s)}
        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 text-slate-700 dark:text-slate-200 text-xs font-semibold disabled:opacity-50"
        data-testid="care-snooze-btn"
      >
        <Clock className="w-3 h-3" /> Snooze
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 z-40 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl py-1 w-32">
            {[3, 7, 14, 30].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => { onPick(d); setOpen(false); }}
                className="block w-full text-left px-3 py-1.5 text-xs hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                {d} day{d > 1 ? 's' : ''}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default CareQueueTab;
